import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {Group, Select, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {forwardRef} from 'react';

import {assert} from 'Asserts';
import {EnumOptions, GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {JSONValue} from 'util/Types';

import CyclingSelector from '../CyclingSelector';
import {EnumUISchema} from '../StepConfigEditorTypes';

interface EnumEditorProps {
  config: string | number | null;
  configShape: GetPipelineConfigSpecType<'enum'>;
  onChangeConfig: (config: JSONValue) => void;
  uiSchema?: EnumUISchema;
  disabled?: boolean;
  buttonSize?: ButtonSize;
  isExpandable?: boolean;
  isExpanded?: boolean;
  dataTestAttrs?: DataTestAttrs;
}
interface ItemProps {
  icon?: BenchlingIconName | undefined;
  label: string;
  description: string;
  svgImagePath?: string;
}

export function isEnumOptionsArray(
  choices: ReadonlyArray<string> | ReadonlyArray<number> | ReadonlyArray<EnumOptions>
): choices is ReadonlyArray<EnumOptions> {
  return choices.every((item) => typeof item === 'object');
}

export default function EnumEditor({
  config,
  configShape,
  onChangeConfig,
  uiSchema,
  disabled,
  isExpandable = false,
  isExpanded = false,
  dataTestAttrs = {},
}: EnumEditorProps): JSX.Element {
  function getEnumOptionDisplayName(enumOption: string | number): string {
    return uiSchema?.displayNames?.[enumOption] ?? String(enumOption);
  }

  const placeholder = uiSchema?.placeholder ?? 'Select an option';
  const isCyclingSelector = uiSchema?.renderCyclingSelector;
  let displayName: string = placeholder;
  let selectedIcon: BenchlingIconName | undefined;
  let selectedImage: string | undefined;
  if (isEnumOptionsArray(configShape.choices)) {
    const matchingChoice = configShape.choices.filter((choice) => String(choice.value) === config)[0];
    if (matchingChoice) {
      displayName = matchingChoice.displayName ?? placeholder;
      selectedIcon = matchingChoice.iconName;
      selectedImage = matchingChoice.svgImagePath;
    }
  } else if (config) {
    displayName = getEnumOptionDisplayName(config);
    selectedIcon = uiSchema?.iconNames?.[config];
    selectedImage = uiSchema?.svgImagePaths?.[config];
  }

  assert(
    !(uiSchema?.svgImagePaths != null && uiSchema?.iconNames != null),
    'Cannot have both svgImagePaths and iconNames defined in uiSchema.'
  );

  const data = isEnumOptionsArray(configShape.choices)
    ? configShape.choices.map((choice) => ({
        label: choice.displayName ?? getEnumOptionDisplayName(choice.value),
        value: String(choice.value),
        icon: choice.iconName,
        svgImagePath: choice.svgImagePath,
      }))
    : configShape.choices.map((choice) => ({
        label: getEnumOptionDisplayName(choice),
        value: String(choice),
        icon: uiSchema?.iconNames?.[choice],
        svgImagePath: uiSchema?.svgImagePaths?.[choice],
      }));

  const selector = (
    <Select
      disabled={disabled}
      placeholder={displayName}
      value={config == null ? config : String(config)}
      allowDeselect={!configShape.required}
      clearable={!configShape.required}
      data={data}
      itemComponent={SelectItem}
      onChange={(value) => {
        onChangeConfig(value ?? null);
      }}
      className={css(styles.grow)}
      classNames={{
        input: css(
          styles.input,
          isExpandable && styles.expandable,
          isExpandable && isCyclingSelector && styles.expandableCyclingSelectorBorder,
          isExpanded && styles.expanded,
          (selectedIcon != null || selectedImage != null) && styles.inputWithIcon
        ),
        dropdown: css(isExpandable && isExpanded && styles.expandableDropdown),
      }}
      searchable={true}
      nothingFound="No options"
      icon={
        (selectedIcon && <BenchlingIcon icon={selectedIcon} className={css(styles.selectedIcon)} />) ||
        (selectedImage && (
          <div className={css(styles.squareImageContainer)}>
            <img src={selectedImage} alt={displayName} className={css(styles.squareImage)} />
          </div>
        ))
      }
      {...getTestProps(dataTestAttrs)}
    />
  );

  return isCyclingSelector ? (
    <div className={css(isExpandable && styles.marginRight)}>
      <CyclingSelector
        selector={selector}
        disabled={disabled}
        options={configShape.choices.map((choice) => choice)}
        selectedOption={config}
        onChange={onChangeConfig}
      />
    </div>
  ) : (
    selector
  );
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({icon, svgImagePath, label, ...others}: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap={true}>
        {svgImagePath && <img src={svgImagePath} alt={label} />}
        {icon && <BenchlingIcon icon={icon} />}
        <div>
          <Text size="sm">{label}</Text>
        </div>
      </Group>
    </div>
  )
);

const styles = StyleSheet.create({
  marginRight: {
    marginRight: '5px',
  },
  grow: {
    flexGrow: 1,
  },
  chevron: {
    color: Colors.GRAY_DARKER,
  },
  input: {
    height: '30px',
    borderRadius: '4px',
  },
  inputWithIcon: {
    paddingLeft: '30px',
  },
  selectedIcon: {
    paddingLeft: '10px',
    color: Colors.BLUE_DARKER,
  },
  expandableDropdown: {
    marginTop: '-9px',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
  },
  expandable: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '5px',
    borderRight: 'none',
    borderTopRightRadius: '0px',
    borderBottomRightRadius: '0px',
  },
  expanded: {
    borderBottomLeftRadius: '0px',
  },
  expandableCyclingSelectorBorder: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '4px',
  },
  squareImageContainer: {
    width: '20px',
    height: '20px',
    marginLeft: '10px',
    flexShrink: 0,
  },
  squareImage: {
    width: '100%',
    height: '100%',
  },
});
